import * as React from "react";
import { toast } from "sonner";
import { Plus, History, Undo2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/** Rate history timeline for a member.
 *   - Read: anyone with hr.view; self always allowed.
 *   - Write: hr.manage. Adds an append-only rate row (with effective_date).
 *   - Supersede: hr.manage. Marks a mistake row superseded (audit trail preserved).
 */
export default function RateHistoryPanel({ companyId, userId, canManage, canView, testidPrefix = "hr-rates" }) {
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({ rate: "", effective_date: "", note: "" });

  const load = React.useCallback(async () => {
    if (!canView) { setRows([]); return; }
    try {
      const { data } = await api.get(`/companies/${companyId}/hr/rates/${userId}`);
      setRows(data.history || []);
    } catch (e) {
      setRows([]);
    }
  }, [companyId, userId, canView]);
  React.useEffect(() => { load(); }, [load]);

  async function add() {
    const cents = Math.round((parseFloat(form.rate) || 0) * 100);
    if (!cents || !form.effective_date || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/hr/rates/${userId}`, {
        hourly_rate_cents: cents,
        effective_date: form.effective_date,
        note: form.note || "",
      });
      setForm({ rate: "", effective_date: "", note: "" });
      toast.success("Rate added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function supersede(row) {
    if (!window.confirm(`Mark rate £${(row.hourly_rate_cents/100).toFixed(2)}/h (from ${row.effective_date}) as superseded?`)) return;
    try {
      await api.post(`/companies/${companyId}/hr/rates/${userId}/${row.id}/supersede`, { reason: "Marked as mistake" });
      toast.success("Superseded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  if (!canView) {
    return <div className="text-[12px] text-brand-muted">You can view your own rate history only.</div>;
  }
  if (rows === null) return <div className="text-[12px] text-brand-muted">Loading rate history…</div>;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-1.5">
        <History size={13} className="text-brand-muted" />
        <div className="micro-label">Rate history</div>
      </div>
      {rows.length === 0 && (
        <div className="text-[12px] text-brand-muted">No rate history yet.</div>
      )}
      {rows.length > 0 && (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.id}
                 className={`flex items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0 ${r.superseded ? 'opacity-50' : ''}`}
                 data-testid={`${testidPrefix}-row-${r.id}`}>
              <span className="mono-num text-[13px] font-semibold text-brand-ink w-20">
                £{(r.hourly_rate_cents / 100).toFixed(2)}
              </span>
              <span className="mono-num text-[11.5px] text-brand-muted w-24">{r.effective_date}</span>
              <span className="flex-1 text-[12px] text-brand-ink truncate">{r.note || "—"}</span>
              {r.superseded && <span className="chip chip-coral">superseded</span>}
              {canManage && !r.superseded && (
                <button className="btn-ghost !h-6 !px-1.5 !text-brand-muted"
                        onClick={() => supersede(r)} title="Mark superseded"
                        data-testid={`${testidPrefix}-supersede-${r.id}`}>
                  <Undo2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="pt-3 border-t border-brand-rule">
          <div className="micro-label mb-2">Add a rate change</div>
          <div className="grid grid-cols-[90px_130px_1fr_auto] gap-2">
            <input className="field-input !h-8 mono-num" placeholder="£/h" type="number" step="0.01" min="0"
                   value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                   data-testid={`${testidPrefix}-rate-input`} />
            <input className="field-input !h-8 mono-num" type="date"
                   value={form.effective_date}
                   onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                   data-testid={`${testidPrefix}-date-input`} />
            <input className="field-input !h-8" placeholder="Note (optional)"
                   value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                   data-testid={`${testidPrefix}-note-input`} />
            <button className="btn-violet !h-8" onClick={add}
                    disabled={busy || !form.rate || !form.effective_date}
                    data-testid={`${testidPrefix}-add-btn`}>
              <Plus size={13} /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
